import { cn } from "../lib/cn";

type ButtonVariant = "primary" | "accent" | "outline" | "ghost" | "danger";
type ButtonSize = "sm" | "md" | "lg";

/** Resting surface + ink per variant. Disabled dimming rides along as a `disabled:` pair. */
const variantBase: Record<ButtonVariant, string> = {
  primary: "bg-ink text-ink-inverse disabled:bg-recess disabled:text-faint",
  accent: "bg-accent text-ink-inverse disabled:bg-recess disabled:text-faint",
  outline:
    "bg-card border border-rule text-ink disabled:bg-recess disabled:text-faint",
  ghost: "bg-transparent text-ink-body disabled:text-faint",
  danger:
    "bg-ink-stopped text-ink-inverse disabled:bg-recess disabled:text-faint",
};

/**
 * Hover is paper lift, not recolor - so every variant gets the same lift and
 * only ghost (which has no resting surface) picks up a wash. The wash is
 * translucent so it reads on any ground, including a `bg-recess` band.
 */
const variantHover: Record<ButtonVariant, string> = {
  primary: "hover:shadow-lift-lg hover:-translate-y-px",
  accent: "hover:shadow-lift-lg hover:-translate-y-px",
  outline: "hover:shadow-lift-lg hover:-translate-y-px",
  ghost: "hover:bg-smudge",
  danger: "hover:shadow-lift-lg hover:-translate-y-px",
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: "gap-1.5 px-2.5 py-1 text-sm",
  md: "gap-2 px-3.5 py-1.5 text-sm",
  lg: "gap-2 px-5 py-2.5 text-base",
};

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** `primary` is the workhorse; `accent` is the indigo one, for the act a
      screen is really about. */
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** In-flight: disables the button, sets `aria-busy`, and shows an inline spinner. */
  busy?: boolean;
  ref?: React.Ref<HTMLButtonElement>;
}

/**
 * The standard button. Flat card stock that lifts on hover; `accent` is the
 * indigo one, kept for the single act a screen is about.
 */
export function Button({
  variant = "primary",
  size = "md",
  busy = false,
  disabled,
  className,
  children,
  type = "button",
  ...rest
}: ButtonProps) {
  const inert = Boolean(disabled) || busy;

  return (
    <button
      type={type}
      disabled={inert}
      aria-busy={busy || undefined}
      className={cn(
        "inline-flex select-none items-center justify-center rounded-paper font-body font-medium",
        "transition duration-150 ease-paper",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
        "disabled:cursor-not-allowed",
        sizeClasses[size],
        variantBase[variant],
        !inert && variantHover[variant],
        className,
      )}
      {...rest}
    >
      {busy && (
        <>
          <span
            aria-hidden="true"
            className={cn(
              "size-3.5 shrink-0 animate-spin rounded-full",
              "border-[1.5px] border-current border-t-transparent",
              "motion-reduce:hidden",
            )}
          />
          {/* Under reduced motion the spinner is replaced by a still ellipsis. */}
          <span
            aria-hidden="true"
            className="hidden font-mono leading-none motion-reduce:inline"
          >
            ...
          </span>
        </>
      )}
      {children}
    </button>
  );
}
